/**
 * Tenant · eCourts Integration Settings
 *
 * Firm-owner-only page to:
 *   - Toggle the firm's master eCourts switch (respects platform master)
 *   - Per-endpoint enable/disable (locked if super-admin force_on/off)
 *   - Per-endpoint role gates (which staff can use which endpoint)
 *   - Auto-import behaviour when creating a new case
 *   - Live passbook of their firm's eCourts spend
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, AlertTriangle, RefreshCw, History, Download, Search, Loader2, CheckCircle2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";
import Modal, { Field, Input, Textarea, GoldButton } from "@/components/Ui";
import CauseListPolicyCard from "@/components/CauseListPolicyCard";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const ROLES = ["firm_owner", "managing_partner", "senior_advocate", "associate_advocate", "junior_advocate", "clerk", "paralegal"];

export default function EcourtsConfig() {
    const [cfg, setCfg] = useState(null);
    const [pb, setPb] = useState([]);

    const load = useCallback(async () => {
        try {
            const [r1, r2] = await Promise.all([
                api.get("/ecourts/config"),
                api.get("/ecourts/passbook", { params: { limit: 50 } }),
            ]);
            setCfg(r1.data);
            setPb(r2.data);
        } catch (e) { toast.error(apiError(e)); }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!cfg) return <div className="text-sm text-muted-foreground">Loading…</div>;

    if (!cfg.platform_enabled) {
        return (
            <>
                <PageHeader title="eCourts Sync" subtitle="Pull case data, hearings & orders directly from eCourts.gov.in"/>
                <div className="bg-muted border border-yellow-700/40 p-6 max-w-2xl">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5"/>
                        <div>
                            <div className="text-sm text-primary">eCourts integration not yet enabled platform-wide</div>
                            <div className="text-xs text-muted-foreground mt-2">Your platform admin hasn't activated the eCourts API connection yet. Please contact support to enable it for your firm.</div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const patchEndpoint = async (key, body) => {
        try { await api.patch(`/ecourts/config/endpoints/${key}`, body); await load(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const patchConfig = async (body) => {
        try { await api.patch(`/ecourts/config`, body); await load(); toast.success("Saved"); }
        catch (e) { toast.error(apiError(e)); }
    };

    return (
        <>
            <PageHeader title="eCourts Sync"
                subtitle="Pull case data, hearings & orders directly from eCourts.gov.in. Costs are billed to your AI Wallet at the platform's configured rate."/>

            <div className="mb-6 flex flex-wrap gap-3">
                <ImportCasesButton onImported={load}/>
                <RefreshAllButton/>
                <CronStatusBadge/>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-8 max-w-5xl">
                <div className="bg-muted border border-border p-6 space-y-4">
                    <div className="overline text-primary">Firm-wide switches</div>
                    <FlexField label="Firm master switch"
                        hint="Disable to block ALL eCourts calls for your firm (saves cost during slow months).">
                        <Toggle on={cfg.enabled} onChange={v => patchConfig({ enabled: v })} testId="firm-master-toggle"/>
                    </FlexField>
                    <FlexField label="On Litigation Create"
                        hint="When a user adds a new case, should we auto-pull from eCourts?">
                        <select value={cfg.auto_import_on_case_create}
                            onChange={e => patchConfig({ auto_import_on_case_create: e.target.value })}
                            data-testid="auto-import-select"
                            className="bg-background border border-border px-3 py-1.5 text-xs">
                            <option value="ask">Ask each time</option>
                            <option value="always">Always auto-fill</option>
                            <option value="never">Never</option>
                        </select>
                    </FlexField>
                </div>

                <div className="bg-muted border border-border p-6">
                    <div className="overline text-primary mb-3">Recent eCourts spend</div>
                    {pb.length === 0
                        ? <div className="text-xs text-muted-foreground">No eCourts calls yet from your firm.</div>
                        : (
                            <div className="space-y-2 text-xs max-h-48 overflow-auto">
                                {pb.slice(0, 10).map(r => (
                                    <div key={r.id} className="flex justify-between items-center" data-testid={`mini-pb-${r.id}`}>
                                        <div>
                                            <div className="text-primary">{r.endpoint_label}</div>
                                            <div className="text-[10px] text-muted-foreground">{r.request_summary} · {new Date(r.created_at).toLocaleString()}</div>
                                        </div>
                                        <div className="font-mono text-right">
                                            <div>₹{r.billed_inr.toFixed(2)}</div>
                                            <div className={`text-[10px] ${r.status === "ok" ? "text-emerald-400" : "text-red-400"}`}>{r.status}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                </div>
            </div>

            <div className="mb-8 max-w-5xl">
                <CauseListPolicyCard/>
            </div>

            <div className="mb-3">
                <div className="overline text-primary">Endpoint controls</div>
                <div className="text-xs text-muted-foreground mt-1">Toggle each capability on/off and restrict which roles in your firm can use it.</div>
            </div>
            <EndpointPrefsTable cfg={cfg} patchEndpoint={patchEndpoint}/>
        </>
    );
}

function EndpointPrefsTable({ cfg, patchEndpoint }) {
    const items = Object.values(cfg.endpoint_prefs || {});
    const ctrl = useTableControls(items, {
        accessors: {
            enabled: (e) => (e.platform_force_on || (e.enabled && !e.platform_force_off)) ? "on" : "off",
            roles: (e) => (e.allowed_roles || []).join(" "),
        },
    });
    return (
        <>
            <FilterChip ctrl={ctrl} className="mb-2"/>
            <div className="overflow-x-auto">
                <table className="w-full text-xs border border-border">
                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                        <tr>
                            <SortableTh field="label" ctrl={ctrl} className="text-left p-3">Endpoint</SortableTh>
                            <SortableTh field="enabled" ctrl={ctrl} className="text-center p-3" align="center">Enabled</SortableTh>
                            <SortableTh field="roles" ctrl={ctrl} className="text-left p-3">Roles allowed</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {ctrl.rows.map(e => {
                            const locked = e.platform_force_on || e.platform_force_off || !e.platform_enabled;
                            const lockReason = e.platform_force_off ? "Force-OFF by super-admin"
                                : e.platform_force_on ? "Force-ON by super-admin (cannot disable)"
                                : !e.platform_enabled ? "Disabled by super-admin"
                                : null;
                            return (
                                <tr key={e.key} className="border-t border-border" data-testid={`tep-row-${e.key}`}>
                                    <td className="p-3">
                                        <div className="flex items-center gap-2 text-primary">
                                            {e.label}
                                            {locked && <Lock className="w-3 h-3 text-muted-foreground" title={lockReason}/>}
                                        </div>
                                        <div className="text-[10px] text-muted-foreground">{e.category}</div>
                                        {locked && <div className="text-[10px] text-yellow-500/80 mt-1">{lockReason}</div>}
                                    </td>
                                    <td className="p-3 text-center">
                                        <Toggle on={e.platform_force_on || (e.enabled && !e.platform_force_off)}
                                            disabled={locked}
                                            onChange={v => patchEndpoint(e.key, { enabled: v })}
                                            testId={`tep-${e.key}-toggle`}/>
                                    </td>
                                    <td className="p-3">
                                        <div className="flex flex-wrap gap-1.5">
                                            {ROLES.map(r => {
                                                const active = e.allowed_roles.includes(r);
                                                return (
                                                    <button key={r} onClick={() => patchEndpoint(e.key, {
                                                        allowed_roles: active ? e.allowed_roles.filter(x => x !== r) : [...e.allowed_roles, r],
                                                    })}
                                                        data-testid={`tep-${e.key}-role-${r}`}
                                                        className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] border ${active ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-muted-foreground"}`}>
                                                        {r.replace(/_/g, " ")}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function FlexField({ label, hint, children }) {
    return (
        <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
                <div className="text-[10px] text-muted-foreground uppercase tracking-[0.16em]">{label}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
            </div>
            <div className="shrink-0">{children}</div>
        </div>
    );
}

function Toggle({ on, onChange, testId, disabled }) {
    return (
        <button type="button" disabled={disabled} onClick={() => !disabled && onChange(!on)}
            data-testid={testId}
            className={`relative inline-flex h-5 w-9 items-center transition-colors ${on ? "bg-primary" : "bg-border"} ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}>
            <span className={`inline-block h-3.5 w-3.5 transform bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`}/>
        </button>
    );
}

/**
 * SearchableClientSelect — drop-in replacement for native <select> when the
 * client list grows. Click the button → dropdown opens with a search field +
 * filtered list. Supports a "compact" mode for in-table use.
 */
function SearchableClientSelect({ value, clients, placeholder = "— pick —", onChange, testId, compact = false }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const ref = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        // Focus search field on open
        setTimeout(() => inputRef.current?.focus(), 0);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const selected = clients.find(c => c.id === value);
    const filtered = q.trim()
        ? clients.filter(c => {
            const needle = q.trim().toLowerCase();
            return (c.name || "").toLowerCase().includes(needle)
                || (c.mobile || "").toLowerCase().includes(needle)
                || (c.email || "").toLowerCase().includes(needle);
          })
        : clients;

    const sizeCls = compact
        ? "text-[10px] px-1.5 py-0.5 min-w-[120px] max-w-[140px]"
        : "text-sm px-3 py-2 w-full";

    return (
        <div className="relative" ref={ref}>
            <button type="button" onClick={() => setOpen(s => !s)}
                data-testid={testId}
                className={`bg-background border border-border hover:border-primary/50 flex items-center justify-between gap-2 text-left ${sizeCls}`}>
                <span className={`truncate ${selected ? "" : "text-muted-foreground"}`}>
                    {selected ? selected.name : placeholder}
                </span>
                <span className="text-primary shrink-0">▾</span>
            </button>
            {open && (
                <div className={`absolute z-40 ${compact ? "right-0 min-w-[240px]" : "left-0 right-0"} mt-1 bg-background border border-border shadow-2xl`}
                     data-testid={`${testId}-popup`}>
                    <div className="p-2 border-b border-border">
                        <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
                            placeholder="Search by name / mobile / email"
                            data-testid={`${testId}-search`}
                            className="w-full bg-muted border border-border px-2 py-1.5 text-xs focus:border-primary outline-none"/>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                        <button type="button"
                            onClick={() => { onChange(""); setOpen(false); setQ(""); }}
                            data-testid={`${testId}-opt-clear`}
                            className="w-full text-left px-3 py-2 text-xs text-muted-foreground hover:bg-muted border-b border-muted">
                            {placeholder}
                        </button>
                        {filtered.length === 0 && (
                            <div className="p-3 text-[10px] text-muted-foreground text-center">No matches.</div>
                        )}
                        {filtered.map(c => (
                            <button key={c.id} type="button"
                                onClick={() => { onChange(c.id); setOpen(false); setQ(""); }}
                                data-testid={`${testId}-opt-${c.id}`}
                                className={`w-full text-left px-3 py-2 text-xs hover:bg-muted border-b border-muted last:border-b-0 ${c.id === value ? "bg-muted text-primary" : ""}`}>
                                <div className="truncate">{c.name}</div>
                                {(c.mobile || c.email) && (
                                    <div className="text-[10px] text-muted-foreground truncate">
                                        {c.mobile}{c.mobile && c.email ? " · " : ""}{c.email}
                                    </div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------- Cron status badge ----------
function CronStatusBadge() {
    const [status, setStatus] = useState(null);
    useEffect(() => {
        api.get("/ecourts/cron/last-status")
            .then(r => setStatus(r.data))
            .catch(() => setStatus(null));
    }, []);
    if (!status) return null;
    const last = status.last_refresh_at ? new Date(status.last_refresh_at) : null;
    return (
        <div className="inline-flex items-center gap-2 px-3 py-2 bg-muted border border-border text-xs" data-testid="cron-status-badge">
            <RefreshCw className="w-3.5 h-3.5 text-primary" strokeWidth={1.5}/>
            <span className="text-muted-foreground">
                {status.synced_case_count} synced case{status.synced_case_count === 1 ? "" : "s"}
                {last ? ` · last refresh ${last.toLocaleString()}` : " · awaiting first refresh"}
            </span>
        </div>
    );
}

// ---------- Manual "Refresh all my cases" with cost estimate + filter chips ----------
const REFRESH_FILTERS = [
    { key: "all",       label: "All",         hint: "Every synced case" },
    { key: "active",    label: "Active",      hint: "Upcoming hearing on record" },
    { key: "past_due",  label: "Past-due",    hint: "Stale next hearing date" },
    { key: "disposed",  label: "Disposed",    hint: "Closed / decided cases" },
    { key: "pending",   label: "Pending",     hint: "No next hearing date yet" },
];

function RefreshAllButton() {
    const [open, setOpen] = useState(false);
    const [estimate, setEstimate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [filter, setFilter] = useState("all");

    const fetchEstimate = async (filterKey) => {
        setLoading(true); setEstimate(null);
        try {
            const r = await api.get("/ecourts/refresh-all/estimate",
                { params: { filter: filterKey } });
            setEstimate(r.data);
        } catch (e) { toast.error(apiError(e)); setOpen(false); }
        finally { setLoading(false); }
    };

    const openModal = async () => {
        setOpen(true); setResult(null); setFilter("all");
        await fetchEstimate("all");
    };

    const switchFilter = async (k) => {
        if (k === filter || loading) return;
        setFilter(k);
        await fetchEstimate(k);
    };

    const runRefresh = async () => {
        setRunning(true);
        try {
            const r = await api.post("/ecourts/refresh-all",
                { confirm: true, filter },
                { timeout: 10 * 60 * 1000 });
            setResult(r.data);
            toast.success(`Batch-checked ${r.data.refreshed} · ${r.data.date_updated} date${r.data.date_updated === 1 ? "" : "s"} updated · ₹${r.data.total_cost_inr}`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunning(false); }
    };

    return (
        <>
            <button type="button" onClick={openModal}
                data-testid="refresh-all-open"
                className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-primary hover:bg-primary/10 text-xs uppercase tracking-[0.16em]">
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={2}/>
                Refresh all my cases
            </button>
            {open && (
                <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={() => !running && setOpen(false)}>
                    <div className="bg-card border border-border w-full max-w-md p-5 space-y-4" onClick={e => e.stopPropagation()}
                         data-testid="refresh-all-modal">
                        <h3 className="text-foreground text-lg">Refresh all synced cases</h3>

                        {/* Filter chips with per-bucket CNR counts */}
                        {!result && (
                            <div className="flex flex-wrap gap-1.5" data-testid="rfa-filter-chips">
                                {REFRESH_FILTERS.map(f => {
                                    const active = filter === f.key;
                                    const count = estimate?.counts?.[f.key];
                                    return (
                                        <button key={f.key} type="button"
                                            onClick={() => switchFilter(f.key)}
                                            disabled={loading || running}
                                            title={f.hint}
                                            data-testid={`rfa-filter-${f.key}`}
                                            className={`text-[10px] uppercase tracking-[0.14em] px-3 py-1.5 border transition inline-flex items-center gap-1.5
                                                ${active ? "border-primary text-primary bg-primary/10"
                                                          : "border-border text-muted-foreground hover:text-foreground hover:border-primary/40"}
                                                disabled:opacity-40`}>
                                            <span>{f.label}</span>
                                            {typeof count === "number" && (
                                                <span data-testid={`rfa-filter-count-${f.key}`}
                                                    className={`text-[9px] tabular-nums font-mono px-1.5 py-0.5 rounded-sm
                                                        ${active ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground/80"}`}>
                                                    {count}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {loading && <div className="text-muted-foreground text-sm">Calculating estimate…</div>}
                        {!loading && estimate && !result && (
                            <>
                                <div className="border border-border bg-background p-4 space-y-2 text-sm">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Cases in scope</span><span className="text-foreground" data-testid="rfa-synced">{estimate.synced_cases}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Past-due (may need `case_detail`)</span><span className="text-amber-500">{estimate.past_due_cases}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Batch @ ₹{estimate.per_cnr_batch_inr.toFixed(2)}/CNR</span><span className="text-foreground">₹{estimate.batch_cost_inr.toFixed(2)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">~{estimate.estimated_fallback_calls} fallback @ ₹{estimate.per_case_detail_inr.toFixed(2)}</span><span className="text-foreground">₹{estimate.estimated_fallback_cost_inr.toFixed(2)}</span></div>
                                    <div className="flex justify-between border-t border-border pt-2 mt-2"><span className="text-foreground font-medium">Estimated total</span><span className="text-primary text-lg" data-testid="rfa-total">₹{estimate.estimated_total_inr.toFixed(2)}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Wallet balance</span>
                                        <span className={estimate.wallet_sufficient ? "text-emerald-400" : "text-rose-400"} data-testid="rfa-wallet">₹{estimate.wallet_balance_inr.toFixed(2)}</span>
                                    </div>
                                    <div className="text-[11px] text-muted-foreground pt-1">
                                        {estimate.chunks} batch call{estimate.chunks === 1 ? "" : "s"} of up to 100 CNRs each · Uses <span className="font-mono">cnr_causelist_batch</span> — updates <strong>next hearing date</strong> in your DB. Judge / bench / stage / parties refresh only when a case&apos;s date changes (delta fallback) — for a full-field refresh on one case, use the per-case &ldquo;🔄 Refresh from eCourts&rdquo; button.
                                    </div>
                                </div>
                                {!estimate.wallet_sufficient && (
                                    <div className="border border-rose-500/40 bg-rose-500/10 text-rose-400 text-xs px-3 py-2">
                                        Wallet balance is below the estimated cost. Top up first, or refresh proceeds but may stop mid-way when funds run out.
                                    </div>
                                )}
                                <div className="flex justify-end gap-2 pt-2">
                                    <button onClick={() => setOpen(false)} disabled={running} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Cancel</button>
                                    <button onClick={runRefresh} disabled={running || estimate.synced_cases === 0}
                                        data-testid="rfa-confirm"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                                        {running ? <Loader2 className="w-3 h-3 animate-spin"/> : <RefreshCw className="w-3 h-3"/>}
                                        {running ? "Refreshing…" : `Confirm · ₹${estimate.estimated_total_inr.toFixed(2)}`}
                                    </button>
                                </div>
                            </>
                        )}
                        {result && (
                            <div className="space-y-3" data-testid="rfa-result">
                                <div className="flex items-center gap-2 text-emerald-400">
                                    <CheckCircle2 className="w-4 h-4"/>
                                    <span className="text-sm">Refresh complete</span>
                                </div>
                                <div className="border border-border bg-background p-3 text-xs space-y-1">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Batch-checked</span><span className="text-foreground">{result.refreshed} CNR{result.refreshed === 1 ? "" : "s"}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Dates updated in DB</span><span className="text-emerald-400" data-testid="rfa-date-updated">{result.date_updated}</span></div>
                                    {result.fallback_used > 0 && (
                                        <div className="flex justify-between"><span className="text-muted-foreground">case_detail fallbacks</span><span className="text-amber-500">{result.fallback_used}</span></div>
                                    )}
                                    <div className="flex justify-between border-t border-border pt-1 mt-1"><span className="text-muted-foreground">Total cost</span><span className="text-primary">₹{result.total_cost_inr}</span></div>
                                    {result.failed_chunks > 0 && (
                                        <div className="flex justify-between"><span className="text-muted-foreground">Failed chunks</span><span className="text-rose-400">{result.failed_chunks}</span></div>
                                    )}
                                </div>
                                <div className="flex justify-end">
                                    <button onClick={() => setOpen(false)} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10">Close</button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

// ---------- Phase 7 — Import my cases ----------
function ImportCasesButton({ onImported }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <button type="button" onClick={() => setOpen(true)}
                data-testid="open-import-modal"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary">
                <Download className="w-3.5 h-3.5" strokeWidth={2}/>
                Import my cases
            </button>
            {open && <ImportCasesModal onClose={() => setOpen(false)} onImported={onImported}/>}
        </>
    );
}

/** Compact multi-select dropdown — shows a button, expands to a checkbox list. */
function MultiSelectDropdown({ label, options, selected, onChange, testId }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const toggle = (val) => {
        const next = new Set(selected);
        if (next.has(val)) next.delete(val); else next.add(val);
        onChange(next);
    };
    const count = selected.size;

    return (
        <div ref={ref} className="relative" data-testid={testId}>
            <button type="button" onClick={() => setOpen(v => !v)}
                className={`flex items-center justify-between gap-2 w-full px-3 py-2 text-xs border bg-background outline-none transition-colors ${count > 0 ? "border-primary text-primary" : "border-border text-foreground hover:border-muted-foreground"}`}>
                <span className="truncate">{count > 0 ? `${label} (${count})` : label}</span>
                <ChevronDown className={`w-3 h-3 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}/>
            </button>
            {open && (
                <div className="absolute z-30 top-full left-0 mt-1 w-full min-w-[180px] max-w-[280px] bg-background border border-border shadow-xl max-h-52 overflow-y-auto">
                    {options.length === 0
                        ? <div className="px-3 py-2 text-xs text-muted-foreground italic">No options</div>
                        : options.map(opt => (
                            <label key={opt} className="flex items-start gap-2 px-3 py-2 hover:bg-muted cursor-pointer text-xs">
                                <input type="checkbox" checked={selected.has(opt)} onChange={() => toggle(opt)}
                                    className="accent-primary mt-0.5 shrink-0"/>
                                <span className="break-words leading-relaxed">{opt}</span>
                            </label>
                        ))
                    }
                </div>
            )}
        </div>
    );
}

function ImportCasesModal({ onClose, onImported }) {
    const navigate = useNavigate();
    const [step, setStep] = useState("search");  // search | results | importing | done
    const [form, setForm] = useState({ advocate_name: "", year: "" });
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [perCnrClient, setPerCnrClient] = useState({});
    const [busy, setBusy] = useState(false);
    const [searchCost, setSearchCost] = useState(null);
    const [importReport, setImportReport] = useState(null);
    const [wallet, setWallet] = useState(null);
    const [showNewClient, setShowNewClient] = useState(false);
    const [newClientCtx, setNewClientCtx] = useState(null);
    const [showNoClientWarning, setShowNoClientWarning] = useState(false);

    // ---- search metadata
    const [variantsSearched, setVariantsSearched] = useState([]);
    const [alreadyImportedCount, setAlreadyImportedCount] = useState(0);
    const [totalVariantsPlanned, setTotalVariantsPlanned] = useState(0);
    const [searchPartial, setSearchPartial] = useState(false);

    // ---- all known eCourts case statuses (fetched once, merged with result-derived ones)
    const [knownStatuses, setKnownStatuses] = useState([]);

    useEffect(() => {
        api.get("/ecourts/enums/case-statuses")
            .then(r => setKnownStatuses(r.data?.statuses || []))
            .catch(() => {});
    }, []);

    // ---- client-side result filters
    const [filterText, setFilterText] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [filterStates, setFilterStates] = useState(new Set());
    const [filterDistricts, setFilterDistricts] = useState(new Set());
    const [filterCourts, setFilterCourts] = useState(new Set());

    // Merge eCourts-known statuses with those found in current results
    const statusOptions = useMemo(() => {
        const set = new Set(knownStatuses);
        results.forEach(r => { if (r.case_status) set.add(r.case_status); });
        return Array.from(set).sort();
    }, [results, knownStatuses]);

    const stateOptions = useMemo(() => {
        const set = new Set();
        results.forEach(r => { if (r.state_name) set.add(r.state_name); });
        return Array.from(set).sort();
    }, [results]);

    const districtOptions = useMemo(() => {
        const set = new Set();
        results.forEach(r => { if (r.district_name) set.add(r.district_name); });
        return Array.from(set).sort();
    }, [results]);

    const courtOptions = useMemo(() => {
        const set = new Set();
        results.forEach(r => { if (r.court_name) set.add(r.court_name); });
        return Array.from(set).sort();
    }, [results]);

    const filteredResults = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        return results.filter(r => {
            if (filterStatus && r.case_status !== filterStatus) return false;
            if (filterStates.size > 0 && !filterStates.has(r.state_name)) return false;
            if (filterDistricts.size > 0 && !filterDistricts.has(r.district_name)) return false;
            if (filterCourts.size > 0 && !filterCourts.has(r.court_name)) return false;
            if (q) {
                const blob = `${r.cnr || ""} ${r.court_case_number || ""} ${r.original_suit_number || ""} ${r.title || ""} ${r.court_name || ""} ${r.state_name || ""} ${r.district_name || ""} ${r.case_status || ""}`.toLowerCase();
                if (!blob.includes(q)) return false;
            }
            return true;
        });
    }, [results, filterText, filterStatus, filterStates, filterDistricts, filterCourts]);

    const clearFilters = () => {
        setFilterText("");
        setFilterStatus("");
        setFilterStates(new Set());
        setFilterDistricts(new Set());
        setFilterCourts(new Set());
    };
    const filtersActive = filterText || filterStatus || filterStates.size > 0 || filterDistricts.size > 0 || filterCourts.size > 0;

    const reloadClients = useCallback(async () => {
        try { const r = await api.get("/clients"); setClients(r.data || []); return r.data; }
        catch { return []; }
    }, []);

    const reloadWallet = useCallback(async () => {
        try { const r = await api.get("/wallet"); setWallet(r.data); }
        catch { setWallet(null); }
    }, []);

    useEffect(() => {
        reloadClients();
        reloadWallet();
    }, [reloadClients, reloadWallet]);

    const runSearch = async () => {
        if (!form.advocate_name.trim()) { toast.error("Advocate name is required"); return; }
        setBusy(true);
        try {
            const r = await api.post("/ecourts/search/advocate", {
                advocate_name: form.advocate_name.trim(),
                year: form.year ? parseInt(form.year) : null,
            }, { timeout: 10 * 60 * 1000 });
            const all = r.data.results || [];
            setResults(all);
            setSearchCost(r.data.cost);
            setVariantsSearched(r.data.variants_searched || []);
            setAlreadyImportedCount(r.data.already_imported_count || 0);
            setTotalVariantsPlanned(r.data.total_variants_planned || 0);
            setSearchPartial(r.data.partial || false);
            setSelected(new Set(all.map(x => x.cnr)));
            clearFilters();
            setStep("results");
            if (r.data.partial) {
                const done = r.data.variants_searched?.length || 0;
                const total = r.data.total_variants_planned || 7;
                toast.warning(
                    all.length > 0
                        ? `Found ${all.length} case${all.length === 1 ? "" : "s"} (${done}/${total} variants — eCourts rate limited)`
                        : `eCourts rate limit hit after ${done}/${total} variants. No new cases in those variants.`
                );
            } else if (all.length === 0) {
                const skipped = r.data.already_imported_count || 0;
                toast.info(skipped > 0
                    ? `No new cases found (${skipped} already in your firm).`
                    : "eCourts returned no matches.");
            } else {
                toast.success(`Found ${all.length} new case${all.length === 1 ? "" : "s"} across ${r.data.variants_count || 1} variant${(r.data.variants_count || 1) === 1 ? "" : "s"}`);
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const toggle = (cnr) => {
        const next = new Set(selected);
        if (next.has(cnr)) next.delete(cnr); else next.add(cnr);
        setSelected(next);
    };

    const _doImport = async () => {
        const estimated = selected.size * 3.0;
        if (wallet && wallet.balance < estimated) {
            toast.error(`Wallet short by ₹${(estimated - wallet.balance).toFixed(2)}. Top up first.`);
            return;
        }
        setBusy(true);
        setStep("importing");
        try {
            const r = await api.post("/ecourts/import/bulk", {
                cnrs: Array.from(selected),
                client_id: clientId || null,
                overrides: perCnrClient,
            }, { timeout: 5 * 60 * 1000 });

            // Bulk imports with 2+ CNRs return status:"queued" — normalise so
            // the "done" step always has imported_count, failed_count, results.
            if (r.data.status === "queued") {
                const normalised = {
                    imported_count: 0,
                    failed_count:   0,
                    skipped_count:  r.data.skipped_count || 0,
                    results:        (r.data.skipped || []).map(s => ({
                        cnr:    s.cnr,
                        status: "skipped",
                        title:  s.reason || "Already imported",
                    })),
                    _queued:  true,
                    job_id:   r.data.job_id,
                    cnr_count: r.data.cnr_count || selected.size,
                    message:  r.data.message || "Import queued — cases will be ready in ~10 minutes.",
                };
                setImportReport(normalised);
                setStep("done");
                toast.success(`Import queued — ${normalised.cnr_count} case${normalised.cnr_count === 1 ? "" : "s"} being fetched in the background`);
            } else {
                setImportReport(r.data);
                setStep("done");
                const n = r.data.imported_count ?? 0;
                toast.success(`Imported ${n} case${n === 1 ? "" : "s"}`);
            }

            onImported?.();
            reloadWallet();
        } catch (e) { toast.error(apiError(e)); setStep("results"); reloadWallet(); }
        finally { setBusy(false); }
    };

    const runImport = () => {
        if (selected.size === 0) { toast.error("Select at least one CNR"); return; }
        if (!clientId) { setShowNoClientWarning(true); return; }
        _doImport();
    };

    const onNewClientCreated = (newClient) => {
        setClients(prev => [...prev, newClient]);
        if (newClientCtx?.cnr) {
            setPerCnrClient(prev => ({ ...prev, [newClientCtx.cnr]: newClient.id }));
        } else {
            setClientId(newClient.id);
        }
        setShowNewClient(false);
        setNewClientCtx(null);
    };

    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-start justify-center p-4 overflow-auto"
             onClick={onClose} data-testid="import-modal-backdrop">
            <div className="bg-background border border-border w-full max-w-4xl my-8 sm:my-12" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 sm:p-5 border-b border-border">
                    <div>
                        <div className="text-lg sm:text-xl font-serif">Import my cases from eCourts</div>
                        <div className="text-[10px] text-muted-foreground mt-1 uppercase tracking-[0.16em]">Step: {step}</div>
                    </div>
                    <button onClick={onClose} data-testid="import-close" className="text-muted-foreground hover:text-foreground text-2xl leading-none p-1">×</button>
                </div>

                <div className="p-4 sm:p-5">
                    {/* ══ STEP: SEARCH ══ */}
                    {step === "search" && (
                        <div className="space-y-4 max-w-xl">
                            <div className="bg-muted/40 border border-border px-4 py-3 text-xs text-muted-foreground space-y-1">
                                <div className="font-medium text-foreground">How it works</div>
                                <div>Enter the advocate name as it may appear in eCourts records. The system will automatically search <span className="text-primary font-medium">up to 7 spelling variations</span> (e.g. "B P Saseendran", "Saseendran B P", "B.P. Saseendran", …) to maximise case coverage.</div>
                                <div className="text-[10px]">Location filters are applied client-side on the results — no extra eCourts cost.</div>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Advocate name *</div>
                                <input value={form.advocate_name} onChange={e => setForm({...form, advocate_name: e.target.value})}
                                    onKeyDown={e => { if (e.key === "Enter") runSearch(); }}
                                    data-testid="import-advocate-input"
                                    placeholder="e.g. B.P.Saseendran"
                                    className="w-full bg-muted border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                            </div>
                            <div>
                                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Year (optional)</div>
                                <input value={form.year} onChange={e => setForm({...form, year: e.target.value})}
                                    placeholder="e.g. 2024"
                                    data-testid="import-year-input"
                                    className="w-48 bg-muted border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                            </div>
                            <button onClick={runSearch} disabled={busy}
                                data-testid="import-search-btn"
                                className="px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary disabled:opacity-40 flex items-center gap-2">
                                {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/> Searching all 7 variants…</> : <><Search className="w-3.5 h-3.5"/> Search eCourts</>}
                            </button>
                        </div>
                    )}

                    {/* ══ STEP: RESULTS ══ */}
                    {step === "results" && (
                        <div className="space-y-4">
                            {/* Header info bar */}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                <span>
                                    {filtersActive
                                        ? <>{filteredResults.length} of {results.length} case{results.length === 1 ? "" : "s"} · <span className="text-primary">{selected.size} selected</span></>
                                        : <>{results.length} case{results.length === 1 ? "" : "s"} found · <span className="text-primary">{selected.size} selected</span></>}
                                </span>
                                {alreadyImportedCount > 0 && (
                                    <span className="text-[10px] text-muted-foreground">{alreadyImportedCount} already in your firm (skipped)</span>
                                )}
                                {variantsSearched.length > 0 && (
                                    <span className={`text-[10px] border px-2 py-0.5 rounded-full ${searchPartial ? "bg-yellow-900/20 border-yellow-700/50 text-yellow-400" : "bg-muted border-border"}`}>
                                        {variantsSearched.length}{searchPartial ? `/${totalVariantsPlanned}` : ""} variant{variantsSearched.length === 1 ? "" : "s"} searched
                                        {searchPartial && " (rate limited)"}
                                    </span>
                                )}
                                {searchCost && <span className="text-[10px]">Search cost: ₹{searchCost.billed_inr.toFixed(2)}</span>}
                            </div>

                            {/* Filter row 1: text + status */}
                            <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto,auto] gap-2 items-center"
                                 data-testid="import-filter-bar">
                                <div className="relative">
                                    <input type="text" value={filterText} onChange={e => setFilterText(e.target.value)}
                                        placeholder="Search CNR · Case No. · Title · Court · State…"
                                        data-testid="import-filter-text"
                                        className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground"/>
                                    {filterText && (
                                        <button type="button" onClick={() => setFilterText("")}
                                            aria-label="Clear search" data-testid="import-filter-text-clear"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-sm leading-none">×</button>
                                    )}
                                </div>
                                <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
                                    data-testid="import-filter-status"
                                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs text-foreground">
                                    <option value="">All statuses</option>
                                    {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <button type="button" onClick={clearFilters} disabled={!filtersActive}
                                    data-testid="import-filter-clear"
                                    className="px-3 py-2 border border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-primary hover:border-primary disabled:opacity-30 whitespace-nowrap">
                                    Clear all
                                </button>
                            </div>

                            {/* Filter row 2: location multi-select dropdowns */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <MultiSelectDropdown
                                    label="State"
                                    options={stateOptions}
                                    selected={filterStates}
                                    onChange={setFilterStates}
                                    testId="import-filter-states"
                                />
                                <MultiSelectDropdown
                                    label="District"
                                    options={districtOptions}
                                    selected={filterDistricts}
                                    onChange={setFilterDistricts}
                                    testId="import-filter-districts"
                                />
                                <MultiSelectDropdown
                                    label="Court"
                                    options={courtOptions}
                                    selected={filterCourts}
                                    onChange={setFilterCourts}
                                    testId="import-filter-courts"
                                />
                            </div>

                            {/* Select all / Deselect all */}
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.16em]">
                                <button type="button"
                                    onClick={() => setSelected(new Set(filteredResults.map(r => r.cnr)))}
                                    data-testid="import-select-all"
                                    className="text-primary hover:underline">
                                    Select all {filtersActive ? "filtered" : ""} ({filteredResults.length})
                                </button>
                                <span className="text-border">·</span>
                                <button type="button" onClick={() => setSelected(new Set())}
                                    data-testid="import-deselect-all"
                                    className="text-muted-foreground hover:text-primary hover:underline">
                                    Deselect all
                                </button>
                            </div>

                            {/* Results table */}
                            <div className="border border-border max-h-64 overflow-auto">
                                <table className="w-full text-xs min-w-[600px]">
                                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.12em] sticky top-0">
                                        <tr>
                                            <th className="text-left p-2 w-8"></th>
                                            <th className="text-left p-2">CNR</th>
                                            <th className="text-left p-2">Case No.</th>
                                            <th className="text-left p-2">Title</th>
                                            <th className="text-left p-2">Court / Location</th>
                                            <th className="text-left p-2">Status</th>
                                            <th className="text-left p-2">Client</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredResults.length === 0 && (
                                            <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                                                {filtersActive ? "No cases match the current filters." : "No matches."}
                                            </td></tr>
                                        )}
                                        {filteredResults.map(r => (
                                            <tr key={r.cnr} className="border-t border-border hover:bg-muted">
                                                <td className="p-2">
                                                    <input type="checkbox" checked={selected.has(r.cnr)}
                                                        onChange={() => toggle(r.cnr)}
                                                        data-testid={`import-row-check-${r.cnr}`}
                                                        className="accent-primary"/>
                                                </td>
                                                <td className="p-2 font-mono text-[10px] whitespace-nowrap">{r.cnr}</td>
                                                <td className="p-2 font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                                                    {r.original_suit_number || r.court_case_number || <span className="text-[#5A5A5A]">—</span>}
                                                </td>
                                                <td className="p-2 max-w-[200px]">
                                                    <div className="truncate">{r.title}</div>
                                                </td>
                                                <td className="p-2 text-muted-foreground text-[10px]">
                                                    <div className="truncate">{r.court_name}</div>
                                                    {(r.district_name || r.state_name) && (
                                                        <div className="text-[9px] text-muted-foreground/70 truncate">
                                                            {[r.district_name, r.state_name].filter(Boolean).join(", ")}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-2 text-[10px] whitespace-nowrap">{r.case_status}</td>
                                                <td className="p-2">
                                                    {selected.has(r.cnr) && (
                                                        <div className="flex items-center gap-1">
                                                            <SearchableClientSelect
                                                                value={perCnrClient[r.cnr] || ""}
                                                                clients={clients}
                                                                placeholder="— default —"
                                                                onChange={(cid) => {
                                                                    const next = { ...perCnrClient };
                                                                    if (cid) next[r.cnr] = cid; else delete next[r.cnr];
                                                                    setPerCnrClient(next);
                                                                }}
                                                                testId={`import-row-client-${r.cnr}`}
                                                                compact
                                                            />
                                                            <button type="button"
                                                                onClick={() => { setNewClientCtx({ cnr: r.cnr }); setShowNewClient(true); }}
                                                                title="New client" data-testid={`import-row-newclient-${r.cnr}`}
                                                                className="text-primary text-[14px] leading-none shrink-0">+</button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Import action bar */}
                            {selected.size > 0 && (
                                <div className="bg-muted border border-border p-4 space-y-3">
                                    <div>
                                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                                                Default client <span className="normal-case tracking-normal text-[10px]">(optional)</span>
                                            </div>
                                            <button type="button" onClick={() => { setNewClientCtx(null); setShowNewClient(true); }}
                                                data-testid="import-new-client-btn"
                                                className="text-[10px] uppercase tracking-[0.16em] text-primary hover:underline inline-flex items-center gap-1">
                                                + New client
                                            </button>
                                        </div>
                                        <SearchableClientSelect
                                            value={clientId}
                                            clients={clients}
                                            placeholder="— no client (import unassigned) —"
                                            onChange={setClientId}
                                            testId="import-client-select"
                                        />
                                        <div className="text-[10px] text-muted-foreground mt-1">
                                            Leave blank to import without a client. You can assign clients later from each case's page.
                                        </div>
                                    </div>
                                    {(() => {
                                        const estimated = selected.size * 3.0;
                                        const balance = wallet?.balance ?? null;
                                        const isShort = balance !== null && balance < estimated;
                                        const overridesUsed = Object.values(perCnrClient).filter(v => v).length;
                                        return (
                                            <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border">
                                                <div className="text-xs text-muted-foreground space-y-0.5">
                                                    <div>
                                                        Estimated cost: <span className="text-primary font-mono">₹{estimated.toFixed(2)}</span>
                                                        <span className="ml-1 text-[10px]">({selected.size} × ₹3.00)</span>
                                                    </div>
                                                    <div>
                                                        Wallet: {balance === null
                                                            ? <span className="text-muted-foreground">—</span>
                                                            : <span className={`font-mono ${isShort ? "text-red-400" : "text-emerald-400"}`}>₹{balance.toFixed(2)}</span>}
                                                        {isShort && <span className="text-red-400 ml-2 text-[10px]">Short by ₹{(estimated - balance).toFixed(2)}</span>}
                                                    </div>
                                                    {overridesUsed > 0 && (
                                                        <div className="text-[10px]">{overridesUsed} per-case client override{overridesUsed === 1 ? "" : "s"}</div>
                                                    )}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2">
                                                    {isShort && (
                                                        <button type="button" onClick={() => { onClose(); navigate("/app/wallet"); }}
                                                            data-testid="import-topup-btn"
                                                            className="px-3 py-2 border border-red-500 text-red-400 text-xs uppercase tracking-[0.16em] hover:bg-red-500 hover:text-foreground">
                                                            Top up
                                                        </button>
                                                    )}
                                                    <button onClick={runImport} disabled={busy || isShort}
                                                        data-testid="import-confirm-btn"
                                                        className="px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary disabled:opacity-40">
                                                        Import {selected.size} case{selected.size === 1 ? "" : "s"}
                                                        {!clientId && <span className="ml-1 normal-case tracking-normal text-[10px] opacity-75">(no client)</span>}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            <button onClick={() => setStep("search")} className="text-[10px] text-muted-foreground hover:text-primary underline" data-testid="import-back">← New search</button>

                            {/* No-client warning popup */}
                            {showNoClientWarning && (
                                <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={() => setShowNoClientWarning(false)}>
                                    <div className="bg-background border border-yellow-700/50 max-w-sm w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
                                        <div className="flex gap-3">
                                            <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5"/>
                                            <div>
                                                <div className="text-sm font-medium mb-2">Import without a client?</div>
                                                <div className="text-xs text-muted-foreground leading-relaxed">
                                                    These {selected.size} case{selected.size === 1 ? "" : "s"} will be imported without any client assignment. Once a client is assigned later, only that client will receive hearing updates and notifications.
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex justify-end gap-2">
                                            <button type="button" onClick={() => setShowNoClientWarning(false)}
                                                data-testid="import-no-client-cancel"
                                                className="px-3 py-2 border border-border text-xs uppercase tracking-[0.16em] text-muted-foreground hover:border-primary">
                                                Cancel
                                            </button>
                                            <button type="button" onClick={() => { setShowNoClientWarning(false); _doImport(); }}
                                                data-testid="import-no-client-confirm"
                                                className="px-4 py-2 bg-yellow-600 text-white text-xs uppercase tracking-[0.16em] hover:bg-yellow-500">
                                                Import anyway
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {step === "importing" && (
                        <div className="text-center py-12">
                            <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" strokeWidth={1.5}/>
                            <div className="text-sm text-muted-foreground">Importing {selected.size} case{selected.size === 1 ? "" : "s"}… each takes ~3s</div>
                        </div>
                    )}

                    {step === "done" && importReport && (
                        <div className="space-y-4">
                            <div className="text-center py-6">
                                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" strokeWidth={1.5}/>
                                <div className="font-serif text-xl">
                                    {importReport._queued ? "Import queued" : "Import complete"}
                                </div>
                                <div className="text-xs text-muted-foreground mt-2">
                                    {importReport._queued
                                        ? importReport.message
                                        : `${importReport.imported_count ?? 0} imported · ${importReport.skipped_count ?? 0} skipped · ${importReport.failed_count ?? 0} failed`}
                                </div>
                            </div>
                            {(importReport.results?.length > 0) && (
                            <div className="max-h-60 overflow-y-auto border border-border">
                                <table className="w-full text-xs">
                                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em] sticky top-0">
                                        <tr>
                                            <th className="text-left p-2">CNR</th>
                                            <th className="text-left p-2">Status</th>
                                            <th className="text-left p-2">Title / Reason</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {importReport.results.map((r, i) => (
                                            <tr key={`${r.cnr}-${i}`} className="border-t border-border">
                                                <td className="p-2 font-mono text-[10px]">{r.cnr}</td>
                                                <td className="p-2">
                                                    <span className={r.status === "imported" ? "text-emerald-400"
                                                                  : r.status === "failed" ? "text-red-400"
                                                                  : "text-yellow-400"}>
                                                        {r.status}
                                                    </span>
                                                </td>
                                                <td className="p-2">{r.title || r.reason || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            )}
                            <div className="flex justify-end gap-2">
                                <button onClick={() => { setStep("search"); setResults([]); setSelected(new Set()); setImportReport(null); }}
                                    data-testid="import-another"
                                    className="px-4 py-2 border border-border text-xs uppercase tracking-[0.16em] hover:border-primary">
                                    Import more
                                </button>
                                <button onClick={onClose}
                                    data-testid="import-done"
                                    className="px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary">
                                    Done
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                {showNewClient && (
                    <NewClientModal onClose={() => { setShowNewClient(false); setNewClientCtx(null); }}
                        onCreated={onNewClientCreated}
                        contextLabel={newClientCtx?.cnr ? `for CNR ${newClientCtx.cnr}` : "(default for all)"}/>
                )}
            </div>
        </div>
    );
}

/**
 * NewClientModal — quick inline client creation that mirrors the full
 * /app/clients "New client" form exactly. Used both for the default client
 * and per-row overrides during bulk import.
 */
function NewClientModal({ onClose, onCreated, contextLabel }) {
    const [form, setForm] = useState({ name: "", mobile: "", email: "", address: "",
                                       district: "", state: "", pincode: "", occupation: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const save = async (ev) => {
        ev?.preventDefault?.();
        if (!form.name.trim()) { toast.error("Client name is required"); return; }
        setBusy(true);
        try {
            const r = await api.post("/clients", form);
            toast.success(`Client "${r.data.name}" created`);
            onCreated(r.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <Modal open={true} onClose={onClose} title="New client" size="lg" testId="ecourts-newclient-modal">
            {contextLabel && (
                <div className="text-[10px] text-muted-foreground uppercase tracking-[0.16em] mb-3" data-testid="newclient-ctx">
                    {contextLabel}
                </div>
            )}
            <form onSubmit={save} className="space-y-4" data-testid="ecourts-newclient-form">
                <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Name"><Input required value={form.name} onChange={upd("name")} data-testid="ecourts-newclient-name" autoFocus/></Field>
                    <Field label="Mobile"><Input value={form.mobile} onChange={upd("mobile")} data-testid="ecourts-newclient-mobile"/></Field>
                    <Field label="Email"><Input type="email" value={form.email} onChange={upd("email")} data-testid="ecourts-newclient-email"/></Field>
                    <Field label="Occupation"><Input value={form.occupation} onChange={upd("occupation")} data-testid="ecourts-newclient-occupation"/></Field>
                    <Field label="District"><Input value={form.district} onChange={upd("district")} data-testid="ecourts-newclient-district"/></Field>
                    <Field label="State"><Input value={form.state} onChange={upd("state")} data-testid="ecourts-newclient-state"/></Field>
                    <Field label="Pincode"><Input value={form.pincode} onChange={upd("pincode")} data-testid="ecourts-newclient-pincode" maxLength={6} inputMode="numeric"/></Field>
                </div>
                <Field label="Address"><Textarea rows="2" value={form.address} onChange={upd("address")} data-testid="ecourts-newclient-address"/></Field>
                <Field label="Notes"><Textarea rows="3" value={form.notes} onChange={upd("notes")} data-testid="ecourts-newclient-notes"/></Field>
                <div className="pt-2 flex justify-end">
                    <GoldButton disabled={busy} data-testid="ecourts-newclient-save">
                        {busy ? "Saving…" : "Add client"}
                    </GoldButton>
                </div>
            </form>
        </Modal>
    );
}
